import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useMemo, useRef } from 'react';
import type { ArticleProps, ArticleRef } from './Article.data';
import styles from './Article.module.scss';

import DynamicComponent from '../../DynamicComponent';
import LatestNews from '../latest-news/LatestNews';
import ArticleHeader from '../../organisms/article-header/ArticleHeader';
import PardotPdfForm from '../pardot-pdf-form/PardotPdfForm';
import { useGlobalStore } from '../../../stores/global';
import Script from 'next/script';
import { formatDate } from '../../../util/dates.utils';
import { useRouter } from 'next/router';

export default function Article({
  className,
  blok,
  canTransitionIn,
  ref,
}: ArticleProps & { ref?: Ref<ArticleRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const globalStore = useGlobalStore();
  const { modules, ...blokNoModules } = blok;
  const hasPardotComponent = useMemo(
    () => modules?.find((module) => module.component === 'pardot_pdf_form'),
    [modules],
  );
  const router = useRouter();
  const store = useGlobalStore();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blok.title,
    image: [blok.key_image?.[0]?.image_portrait, blok.key_image?.[0]?.image_landscape].filter(
      (x) => x,
    ),
    datePublished: formatDate(blok.publish_date, 'YYYY-MM-DD'),
    publisher: {
      '@type': 'Organization',
      name: 'Dentsu Creative',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${process.env.SITE_URL}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'News',
        item: `${process.env.SITE_URL}/news`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: blok.title,
        item: `${process.env.SITE_URL}/${router.asPath}`,
      },
    ],
  };

  const pardotComponentBlokData = useMemo(() => {
    if (!!blok.pardot_id) {
      return {
        pardot_id: blok.pardot_id,
        success_title:
          blok.pardot_title || globalStore.globalSettings.form_pardot_pdf_success_title,
        success_description:
          blok.pardot_success_description ||
          globalStore.globalSettings.form_pardot_pdf_success_description,
        imageBack: blok.pardot_image_back,
        imageFront: blok.pardot_image_front,
        title: blok.pardot_title || globalStore.globalSettings.form_pardot_pdf_title,
        description:
          blok.pardot_description || globalStore.globalSettings.form_pardot_pdf_description,
        button_label: blok.pardot_button_label,
        disclaimer: blok.pardot_disclaimer || globalStore.globalSettings.form_pardot_pdf_disclaimer,
        pardot_input_fields: blok.pardot_input_fields,
        pardot_checkboxes: blok.pardot_checkboxes,
        component: 'pardot_pdf_form' as const,
        _uid: blok._uid,
        linkedin_tracking_id:
          blok.linkedin_tracking_id || globalStore.globalSettings.linkedin_tracking_id,
        use_accent_color: blok.use_accent_color,
      };
    }
    return undefined;
  }, [
    blok._uid,
    blok.pardot_description,
    blok.pardot_disclaimer,
    blok.pardot_id,
    blok.pardot_image_back,
    blok.pardot_image_front,
    blok.pardot_title,
    blok.pardot_success_description,
    blok.pardot_checkboxes,
    blok.pardot_input_fields,
    blok.linkedin_tracking_id,
    blok.pardot_button_label,
    globalStore.globalSettings.form_pardot_pdf_description,
    globalStore.globalSettings.form_pardot_pdf_disclaimer,
    globalStore.globalSettings.form_pardot_pdf_success_description,
    globalStore.globalSettings.form_pardot_pdf_success_title,
    globalStore.globalSettings.form_pardot_pdf_title,
    globalStore.globalSettings.linkedin_tracking_id,
    blok.use_accent_color,
  ]);

  return (
    <article
      ref={elementRef}
      className={classNames(styles.article, className)}
      {...sbEditable(blok)}
    >
      <ArticleHeader
        blok={{
          ...blokNoModules,
          publish_date: blok.publish_date || blok.published_at,
        }}
        className={styles.articleHeader}
        canTransitionIn={canTransitionIn}
      />

      {modules &&
        modules.map((module) =>
          module.component === 'pardot_pdf_form' ? (
            pardotComponentBlokData && (
              <PardotPdfForm
                key={module._uid}
                canTransitionIn={canTransitionIn}
                blok={pardotComponentBlokData}
              />
            )
          ) : (
            <DynamicComponent
              key={module._uid}
              blok={module}
              canTransitionIn={canTransitionIn}
              className={classNames({
                [styles.richText]: module.component === 'rich_text',
                [styles.quote]: module.component === 'quote_block',
                [styles.imageBlock]: module.component === 'image_block',
                [styles.videoBlock]: module.component === 'video_block',
              })}
            />
          ),
        )}

      {!hasPardotComponent && pardotComponentBlokData && (
        <PardotPdfForm canTransitionIn={canTransitionIn} blok={pardotComponentBlokData} />
      )}

      <LatestNews
        canTransitionIn={canTransitionIn}
        blok={{
          _uid: blok._uid,
          component: 'latest_news',
          title: store.globalSettings.label_latest_news || 'Latest News',
          layout: 'column',
        }}
      />

      <Script
        id="organization-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd),
        }}
      />

      <Script
        id="organization-schema-breadcrumbs"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </article>
  );
}
